import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../../services/Toast.service";
import {StateService} from "../../../services/StateService";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {ActivatedRoute, Router} from "@angular/router";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {State} from "../../../models/State";
import {takeUntil} from "rxjs/operators";
import {Session} from "../../../services/Session";
import {PageTitleService} from "../../../services/PageTitle";
import {TemplatesService} from "../../../tpiq-services/templates.service";
import {environment} from "../../../../environments/environment";
import {TrackerPaginatedTableComponent} from "../../../tables/tracker-paginated-table/tracker-paginated-table.component";
import {CreateOrUpdateTemplateComponent} from "../create-or-update-template/create-or-update-template.component";
import {TemplateInfoComponent} from "../template-info/template-info.component";

declare const _: any;

@Component({
  selector: 'app-templates-list',
  standalone: true,
  imports: [CommonModule, FormsModule, TrackerPaginatedTableComponent, CreateOrUpdateTemplateComponent, TemplateInfoComponent],
  templateUrl: './templates-list.component.html',
  styleUrl: './templates-list.component.scss'
})
export class TemplatesListComponent implements OnInit, OnDestroy {
  @Input() strategyId: any;
  @Input() strategyMode: any;
  @Input() templates: any;
  @Output() triggerRefreshOnUpdateTemplate: EventEmitter<any> = new EventEmitter<any>();
  templatesTableData: any = {};
  hasTemplatesList = false;
  org: string = Session.getOrg();
  user: any = {};
  templatesSort: any = {};
  orderArray: any = {
    Name: 'name',
    Strategies: 'strategyCount',
    Rates: 'rateCount',
    Fees: 'feeCount'
  };

  destroy$: EventEmitter<void> = new EventEmitter<void>();
  currentEnvironment: any;
  searchObj: any = {};
  reveal: any = {
    showNotes: false,
    viewTemplate: false,
    addOrEditTemplate: false
  };
  template: any;
  editorOptions = {
    heightMin: 100,
    heightMax: 100,
    toolbarButtons: ['bold', 'italic', 'underline', 'fontFamily', 'fontSize', 'color', 'insertLink', 'outdent', 'indent'],
    toolbarSticky: true,
    toolbarVisibleWithoutSelection: false,
    toolbarInline: true,
    fontFamilySelection: true,
    placeholderText: 'Template notes',
    charCounterCount: false,
    colorsText: ['#14B5F0', '#3574e3', '#DFCF6A', '#2D62BF', '#09A4DE', '#475E79', '#3474e3', '#102D79', '#434343'],
    events : {},
    key: environment.froalaKey
  };
  templateId: any;

  editMode = false;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private titleService: PageTitleService,
    private loadingService: LoadingService,
    private stateService: StateService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private templatesService: TemplatesService
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    this.currentEnvironment = environment;
    if (!this.strategyId) {
      this.titleService.setPageTitle('');
      this.getTemplatesNextSet(true);
    } else {
      this.buildTemplatesTableData(this.templates || [], true);
    }
    this.switchBoard.onPageChangeListener.next({
      url: this._route.url,
      name: 'Strategy Templates',
      id: 'TEMPLATES'
    });
    this.switchBoard.onToastConfirm
      .pipe(takeUntil(this.destroy$)).subscribe(toast => {
      if (toast.data && toast.data.isPublishTemplate) {
        this.publishTemplateToAllInstances(toast.data);
      } else if (toast.data) {
        this.deleteTemplate(toast.data);
      }
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  publishTemplateToAllInstances(data) {
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.publishTemplate(this.org, this.user.id, data.id).subscribe(response => {
      if (response && response.message) {
        this.toastService.pop(TOAST_TYPE.success, response.message);
      } else {
        this.toastService.pop(TOAST_TYPE.success, 'Publish Successfully');
      }
      this.loadingService.triggerLoadingEvent(false);
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      try {
        const message = JSON.parse(error).message || 'Failed To Publish';
        this.toastService.pop(TOAST_TYPE.error, message);
      } catch (err) {
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Publish');
      }
    });
  }

  getTemplatesNextSet(refreshTable){
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.getTemplatesList(this.org, this.user.id)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.buildTemplatesTableData(response, true);
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get templates');
      });
  }

  addNewTemplate(){
    this.editMode = false;
    this.reveal.addOrEditTemplate = true;
  }

  handleScroll(event){
  }

  handleAction($event){
    this.templateId = $event.id;
    if ($event.action == 'edit'){
      if (!this.strategyId) {
        this.editMode = true;
        this.reveal.addOrEditTemplate = true;
      } else {
        if (this.strategyMode && this.strategyMode == 'edit') {
          this.reveal.viewTemplate = true;
        }
      }
    } else if ($event.action == 'delete'){
      this.removeTemplate($event);
    } else if ($event.action == 'notes'){
      this.getTemplate($event.id);
    } else if ($event.action === 'publish') {
      this.publishTemplate($event);
    }
  }

  publishTemplate(data) {
    const tempRowData = _.cloneDeep(data);
    tempRowData.isPublishTemplate = true;
    delete tempRowData.action;
    delete tempRowData.actions;
    this.toastService.pop(TOAST_TYPE.confirm, 'Do you wish to publish this template to all instances?', tempRowData);
  }

  getTemplate(templateId){
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.getTemplate(this.org, this.user.id, templateId).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.template = response;
      this.reveal.showNotes = true;
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, 'Failed to get template info');
    });
  }

  removeTemplate($event) {
    this.toastService.pop(TOAST_TYPE.confirm, 'Are you sure you want to delete?', $event);
  }

  deleteTemplate(template){
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.deleteTemplate(this.org, this.user.id, template.id)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Template deleted successfully');
        this.getTemplatesNextSet(true);
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to delete template');
      });
  }

  sortByColumn(obj){
  }

  resetSortOrder(sortOrder, columns, column) {
    _.each(columns, function(col){
      if (col.name !== column) {
        sortOrder[col.name] = '';
      }
    });
  }

  buildTemplatesTableData(data, refreshTable){
    const base = this;
    this.templatesTableData.options = {
      height: '76vh',
      selectorString: 'id',
      showSearch: false,
    };
    this.templatesTableData.columns = [];
    this.templatesTableData.columns = this.formatColumnsForTable(this.orderArray);
    this.templatesTableData.columns.push({name: 'actions', classes: 'overflow-css', title: '', type: 'html', sortable: false});
    if (refreshTable) {
      this.templatesTableData.rows = [];
    } else {
      this.templatesTableData.rows = this.templatesTableData.rows || [];
    }
    const rows = base.templatesTableData.rows || [];
    data.forEach(function(tax){
      const row = base.createTableRow(tax);
      rows.push(row);
    });
    this.templatesTableData = {...this.templatesTableData, rows};
    this.handleSortOrder(this.templatesSort, this.templatesTableData.columns);
    if (refreshTable) {
      this.hasTemplatesList = false;
    }
    this.loadingService.triggerLoadingEvent(false);
    base.hasTemplatesList = true;
  }

  createTableRow(user) {
    const base = this;
    const row: any = {};
    _.each(user, function(value, key){
      if (key == 'name') {
        row[key] = `<a class='actionOnId' data-action='edit'><span class='icon'>${user[key]}</span></a>`;
        row['templateName'] = user[key];
      } else {
        row[key] = user[key];
      }
    });
    row.actions = '';
    if (!this.strategyId) {
      row.actions += (this.org == 'trialtaxplaniq' || this.org == 'test-taxplaniq' || this.org == 'stag-taxplaniq') ? `<a class='action' data-action='publish'><span class='icon badge je-badge'>Publish</span></a>` : ``;
      row.actions += `<span class='badge-tooltip'><a class='action' data-action='notes'><i class="material-icons strategy-action-icon">short_text</i><span class='bottomTooltiptext' style='font-size: 13px !important;width: 120px !important;left: 0px !important;'>Template Notes</span></a></span>`;
      row.actions += `<a class='action' data-action='delete'><span class='todo-del'><i class="material-icons delete">delete</i></span></a>`;
    }
    return row;
  }

  formatColumnsForTable(data) {
    const base = this;
    const colsArray = [];
    let orderArray;
    if (typeof data !== 'string') {
      orderArray = data;
    } else {
      orderArray = JSON.parse(data);
    }
    _.each(Object.keys(orderArray), function(key) {
      if (key === 'Name') {
        colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: false});
      } else {
        colsArray.push({name: orderArray[key], title: key, type: 'normal', classes: 'pointer zero-overflow', sortable: false});
      }
    });
    return colsArray;
  }

  handleSortOrder(sortOrder, columns) {
    _.each(columns, function(column){
      column.sortType = sortOrder[column.name];
    });
  }

  addState(){
    this.stateService.addState(new State('tax-planning-templates', this._router.url, null, null));
  }

  handleRowClick($event) {
  }

  hideReveal(key){
    this.reveal[key] = false;
  }

  updateNotesForTemplate(){
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.updateTemplate(this.org, this.user.id, this.template).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.success, 'Updated successfully');
      this.reveal.showNotes = false;
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      try{
        this.toastService.pop(TOAST_TYPE.error, error.message ? error.message : 'Failed to update template');
      } catch (e){
        this.toastService.pop(TOAST_TYPE.error, 'Failed to update template');
      }
    });
  }

  triggerOnUpdateTemplate() {
    this.reveal.viewTemplate = false;
    this.triggerRefreshOnUpdateTemplate.emit();
  }

  refreshListOfTemplates() {
    this.reveal.addOrEditTemplate = false;
    this.getTemplatesNextSet(true);
  }

}
